import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import TuneIcon from '@mui/icons-material/Tune';
import { Box } from '@mui/material';
import { Snackbar } from '@/components/shared/Snackbar';

import { BasicTabs } from '@/components/shared/BasicTabs';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { EchoChat } from '@/components/shared/EchoChat';
import { FilterSideBar } from '@/components/Analysis/shared/FilterSideBar/FilterSideBar';
import { QueryReport } from '../QueryReport';
import { EquipmentTable } from '../EquipmentTable';
import { PointOfInterestsContainer } from './PointOfInterests';
import { PaintBlockContainer } from './PaintBlock';
import { AssemblyTabs } from './AssemblyTabs';
import { DecksContainer } from './Decks';
import { EquipmentsContainer } from './Equipments/EquipmentsContainer';
import { SelectedPoiContent } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent';
import { PoiEditingForms } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/PoiEditingForms/PoiEditingForms';
import { EmptyStatePlaceholder } from '../../shared/EmptyStatePlaceholder';
import {
  PoiTemplateSelectorModal,
  ConfirmationDialog,
} from '../../modules/pointOfInterest/BulkUpload/components';
import { IncorrectHeadersDialog } from '../../modules/pointOfInterest/BulkUpload/components/IncorrectHeadersDialog';
import { ValidationsErrorDialog } from '../../modules/pointOfInterest/BulkUpload/components/ValidationsErrorDialog';
import { Container, InsightsContainer, tabStyles, style } from './styles';
import { QUERY_DRAWER_COLOR } from '@/constants';
import { Permissions, InsightsFeatures } from '@/types';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import * as analysisState from '@/components/Analysis/state';
import { statesLoaded } from '@/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import useAbility from '@/hooks/useAbility';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHavePermission } from '@/hooks';
import { useInsightsTabUrlSync } from '../deeplink';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { urlToSVG } from '@/utils';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_EXPLORER_FETCH_SVG_ERROR,
  INSIGHTS_EXPLORER_FETCH_TEMPLATE_ERROR,
  STRUCTURE_ID_MISSING_ERROR,
} from '@/constants/Errors';
import { ReviewScreen } from '../../ReviewScreen/ReviewScreen';
import { useFilterSyncWithParms } from '@/hooks/useFilterSyncWithParms';
import { usePoiTypeMapping } from '@/hooks/usePoiTypeMapping';
import { PaintBlockHistory } from './PaintBlock/PaintBlockHistory';
import { KpiPanelDrawer } from '@/modules/KpiTracker/components/Panel/KpiPanelDrawer';
import { useEditComponentTable } from '@/hooks/useEditComponentTable';

type ComponentsTabContentProps = {
  // string |å number | boolean | null;
  saveSingleEdit?: (itemId: string, columnKey: string, value: string | unknown) => Promise<void>;
  saveBulkEdit?: (columnKey: string, value: unknown, itemIds: string[]) => Promise<void>;
};

/** Keeps a stable element reference for BasicTabs so filter fetches don't remount the whole table. */
const ComponentsTabContent = ({ saveSingleEdit, saveBulkEdit }: ComponentsTabContentProps) => {
  const insightsExplorerLoading = useAtomValue(analysisState.insightsExplorerLoadingState);
  return (
    <EquipmentsContainer loading={insightsExplorerLoading}>
      <QueryReport />
      <EquipmentTable saveSingleEdit={saveSingleEdit} saveBulkEdit={saveBulkEdit} />
    </EquipmentsContainer>
  );
};

export const InsightsExplorer = () => {
  const [isLeftDrawerOpen, setIsLeftDrawerOpen] = useState<boolean>(true);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const insightsExplorerLoading = useAtomValue(analysisState.insightsExplorerLoadingState);
  const structureId = useGetStructureId();
  if (!structureId) {
    ErrorLogger.log(STRUCTURE_ID_MISSING_ERROR.withCode('FABW-INS-007'));
  }
  const [snackbarMessage, setSnackbarMessage] = useAtom(analysisState.snackbarMessage);

  const setPoiTemplateIcons = useSetAtom(analysisState.poiTemplateIcons);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const setStatesLoaded = useSetAtom(statesLoaded);
  const setSelectedSpherical = useSetAtom(analysisState.selectedSpherical);
  const setCurrentSpherical = useSetAtom(analysisState.viewerCurrentSpherical);
  const setApplyFilters = useSetAtom(analysisState.applyFilters);
  const structureInfo = useAtomValue(analysisState.structureFeatures);
  const { data, error: fetchTemplateError } = useFetchTemplatesByType();

  // Fetch POI templates and populate type-to-color/icon mappings once at parent level
  usePoiTypeMapping();
  const { leftDrawerWidth, rightDrawerWidth } = useDrawerWidths();
  const navbarHeight = useNavbarHeight();
  const screenSize = useWindowSize();

  const { ability } = useAbility();
  const isBetaUser = useFeatureFlag('beta-user');
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);
  const { saveSingleEdit, saveBulkEdit } = useEditComponentTable();
  const canReadPOI = ability.can('read', 'PointOfInterest');
  const setAllPointOfInterestTemplates = useSetAtom(analysisState.allPointOfInterestTemplates);
  useFilterSyncWithParms();
  useEffect(() => {
    setApplyFilters(true);
    setCurrentSpherical(undefined);
    setSelectedSpherical(undefined);
    setStatesLoaded(false);
  }, [setApplyFilters, setCurrentSpherical, setSelectedSpherical, setStatesLoaded]);

  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    poiState.selectedPointOfInterestId
  );

  const [selectedMainContainerTab, setSelectedMainContainerTab] = useState<number>(0);

  const canManagePaintBlocks = useHavePermission(Permissions.MANAGE_PAINT_REGIONS);

  const changeMainContainerTab = useCallback((selectedTabIndex: number) => {
    setSelectedMainContainerTab(selectedTabIndex);
  }, []);

  useInsightsTabUrlSync(selectedMainContainerTab, setSelectedMainContainerTab);

  useEffect(() => {
    if (fetchTemplateError) {
      ErrorLogger.log(
        INSIGHTS_EXPLORER_FETCH_TEMPLATE_ERROR.withContext({ structureId }).withError(
          fetchTemplateError
        )
      );
    }
  }, [fetchTemplateError, structureId]);

  useEffect(() => {
    const allTemplates = data?.allPointOfInterestTemplate;
    if (!allTemplates) return;

    setAllPointOfInterestTemplates(allTemplates);
  }, [data?.allPointOfInterestTemplate, setAllPointOfInterestTemplates]);

  useEffect(() => {
    const allTemplates = data?.allPointOfInterestTemplate;
    if (!allTemplates) return;

    const withIcons = allTemplates.filter((template) => template.icon?.name);
    const svgPromises = withIcons.map((template) =>
      urlToSVG(getCloudfrontUrl(template.icon?.name ?? ''))
    );

    (async () => {
      try {
        const svgs = await Promise.all(svgPromises);

        const svgData = withIcons.map((template, icon) => ({
          name: template.name,
          icon: svgs[icon],
          color: template.icon?.color ?? '',
          key: template.key ?? '',
        }));

        setPoiTemplateIcons(svgData);
      } catch (err) {
        ErrorLogger.log(
          INSIGHTS_EXPLORER_FETCH_SVG_ERROR.withContext({ withIcons, svgPromises }).withError(err)
        );
      }
    })();
  }, [data?.allPointOfInterestTemplate, setPoiTemplateIcons]);

  const handleLeftDrawerToggle = useCallback(() => {
    setIsLeftDrawerOpen((current) => !current);
  }, []);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };
  const { shouldShow, content } = snackbarMessage;

  const insightsFeatures: string[] = useMemo(() => {
    if (!structureInfo?.features?.insights) return [];
    return (
      (Array.isArray(structureInfo?.features?.insights) && structureInfo?.features?.insights) ?? []
    );
  }, [structureInfo?.features?.insights]);

  const componentsTab = useMemo(
    () => ({
      label: 'Components',
      flag: InsightsFeatures?.COMPONENTS_TAB,
      component: (
        <ComponentsTabContent saveSingleEdit={saveSingleEdit} saveBulkEdit={saveBulkEdit} />
      ),
      visibilityPermission: true,
    }),
    [saveSingleEdit, saveBulkEdit]
  );

  const poiTab = useMemo(
    () => ({
      label: 'Points of Interest',
      flag: InsightsFeatures?.POINTS_OF_INTEREST_TAB,
      component: <PointOfInterestsContainer />,
      visibilityPermission: canReadPOI,
      testid: 'sidepanel-poi-tab',
    }),
    [canReadPOI]
  );

  const paintBlockTab = useMemo(
    () => ({
      label: 'Paint Blocks',
      flag: InsightsFeatures?.PAINT_BLOCK_TAB,
      component: <PaintBlockContainer />,
      visibilityPermission: canManagePaintBlocks,
    }),
    [canManagePaintBlocks]
  );

  const decksTab = useMemo(
    () => ({
      label: 'Decks',
      flag: InsightsFeatures?.DECK_TAB,
      component: <DecksContainer />,
      visibilityPermission: isBetaUser,
    }),
    [isBetaUser]
  );

  const mainContainerTabs = useMemo(() => {
    const allTabs = [componentsTab, poiTab, paintBlockTab, decksTab];

    const visible = allTabs.filter(
      (tab) => tab.visibilityPermission && insightsFeatures?.includes(tab.flag)
    );

    const computed = visible.map((tab, index) => ({ ...tab, index }));
    return computed;
  }, [componentsTab, poiTab, paintBlockTab, decksTab, insightsFeatures]);

  const selectedTabFlag = useMemo(() => {
    const flag = mainContainerTabs.at(selectedMainContainerTab)?.flag;
    return flag;
  }, [mainContainerTabs, selectedMainContainerTab]);

  const selectedPaintBlockHistory = useAtomValue(analysisState.selectedPaintBlockHistory);

  const isRightDrawerOpen = useMemo(() => {
    return (
      selectedTabFlag === InsightsFeatures.POINTS_OF_INTEREST_TAB ||
      (selectedTabFlag === InsightsFeatures.COMPONENTS_TAB && !canViewReviewScreen) ||
      (selectedTabFlag === InsightsFeatures.PAINT_BLOCK_TAB && !!selectedPaintBlockHistory?.id)
    );
  }, [canViewReviewScreen, selectedTabFlag, selectedPaintBlockHistory]);

  const closeHandler = useCallback(() => {
    setSelectedPointOfInterestId(undefined);
  }, [setSelectedPointOfInterestId]);

  const showPointOfInterestTab = selectedTabFlag === InsightsFeatures.POINTS_OF_INTEREST_TAB;

  useEffect(() => {
    if (!insightsExplorerLoading) {
      setInitialLoad(false);
    }
  }, [insightsExplorerLoading]);

  const selectedPoiDisplayComponent = useMemo(() => {
    if (!selectedPointOfInterestId) {
      return (
        <Box sx={{ marginTop: 'calc(60% + 60px)' }}>
          <EmptyStatePlaceholder type="poiTable" />
        </Box>
      );
    }

    return (
      <SelectedPoiContent
        selectedPoiId={selectedPointOfInterestId.toString()}
        closeHandler={closeHandler}
        disableEdit
        sx={style}
      />
    );
  }, [closeHandler, selectedPointOfInterestId]);

  const tabsListRightMargin = useMemo(() => {
    if (selectedTabFlag === InsightsFeatures.PAINT_BLOCK_TAB) {
      if (screenSize.width && screenSize.width <= 1728) {
        return '96px';
      }
      return '136px';
    }

    return '0px';
  }, [screenSize.width, selectedTabFlag]);

  const showPaintBlockHistory =
    selectedTabFlag === InsightsFeatures.PAINT_BLOCK_TAB && !!selectedPaintBlockHistory?.id;

  const sideDrawerComponent = useMemo(() => {
    if (isRightDrawerOpen) {
      let drawerContent;

      if (showPaintBlockHistory) {
        drawerContent = <PaintBlockHistory />;
      } else if (showPointOfInterestTab) {
        drawerContent = selectedPoiDisplayComponent;
      } else {
        drawerContent = <AssemblyTabs />;
      }

      return (
        <SideDrawer
          position="right"
          width={rightDrawerWidth}
          isOpen={isRightDrawerOpen}
          handleOpen={() => {}}
          sx={{
            width: rightDrawerWidth,
            flexShrink: 0,
            zIndex: 0,
          }}
          sxPaper={{ overflowY: 'hidden' }}
        >
          {drawerContent}
        </SideDrawer>
      );
    }
    if (selectedTabFlag === InsightsFeatures.COMPONENTS_TAB && canViewReviewScreen) {
      return <ReviewScreen />;
    }
    return null;
  }, [
    isRightDrawerOpen,
    rightDrawerWidth,
    showPointOfInterestTab,
    showPaintBlockHistory,
    selectedPoiDisplayComponent,
    selectedTabFlag,
    canViewReviewScreen,
  ]);

  return (
    <Container>
      {!(selectedTabFlag === InsightsFeatures.COMPONENTS_TAB && canViewReviewScreen) && (
        <EchoChat isRightDrawerOpen={isRightDrawerOpen} />
      )}
      <Box>
        <SideDrawer
          isOpen={isLeftDrawerOpen}
          handleOpen={handleLeftDrawerToggle}
          position="left"
          width={leftDrawerWidth}
          label="Query"
          iconStyles={{ top: 10 }}
          showCloseButton
          drawerColor={QUERY_DRAWER_COLOR}
          allDrawersClosed={!isLeftDrawerOpen}
          sx={{
            width: leftDrawerWidth,
            flexShrink: 0,
            zIndex: 0,
          }}
          {...(isLeftDrawerOpen ? {} : { icon: <TuneIcon /> })}
        >
          <FilterSideBar
            showDynamicFilters={
              selectedTabFlag &&
              [InsightsFeatures.COMPONENTS_TAB, InsightsFeatures.POINTS_OF_INTEREST_TAB].includes(
                selectedTabFlag
              )
            }
          />
        </SideDrawer>
        <InsightsContainer
          isLeftDrawerOpen={isLeftDrawerOpen}
          isRightDrawerOpen={isRightDrawerOpen}
          navbarHeight={navbarHeight}
          style={{
            position: 'relative',
            marginLeft: isLeftDrawerOpen ? `${leftDrawerWidth}px` : '64px',
            marginRight: (() => {
              if (isRightDrawerOpen) {
                return `${rightDrawerWidth}px`;
              }
              if (
                selectedTabFlag &&
                [InsightsFeatures.PAINT_BLOCK_TAB, InsightsFeatures.DECK_TAB].includes(
                  selectedTabFlag
                )
              ) {
                return '0px';
              }
              return '32px';
            })(),
          }}
        >
          {/* Substitute with Table loader to avoid loader getting stuck . To be aadded back in if needed later*/}
          {/* {initialLoad && (
            <Box sx={{ position: 'relative', top: '107px' }}>
              <PageLoader />
            </Box>
          )} */}
          <BasicTabs
            renderingStrategy="eager"
            tabs={mainContainerTabs}
            selectedTab={selectedMainContainerTab}
            changeTab={changeMainContainerTab}
            fullWidth={false}
            hidden={initialLoad}
            tabsListRightMargin={tabsListRightMargin}
            tabsStyles={tabStyles}
          />
        </InsightsContainer>
        {sideDrawerComponent}
        <KpiPanelDrawer />
      </Box>

      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
      {editPointOfInterest?.pointOfInterest && (
        <PoiEditingForms
          open={editPointOfInterest.state === 'WaitingForDetail'}
          defaultDrawerWidth={rightDrawerWidth}
        />
      )}
      <PoiTemplateSelectorModal />
      <ConfirmationDialog />
      <IncorrectHeadersDialog />
      <ValidationsErrorDialog />
    </Container>
  );
};
